import dataModel from '../../models/DataModel';
import FileUtil from '../../helpers/FileUtil/FileUtil';
import ImageTools from '../../helpers/ImageTools';
import Metadata from '../../helpers/metadata/Metadata';
import MiscConstants from '../../constants/MiscConstants';
import MobileFiles from '../../helpers/MobileFiles';
import { getFileObject } from '../../helpers/CordovaHelpers';
import _ from 'lodash';

/**
 * PhotoUtilPlugin: 
 * Utility for manipulating photos and photo uploads via cordova plugins
 */

//mobile
//component is the this of the component that is passed in
function handleFileSelect(e) {
  let file = e.target.files[0];
  let extension = FileUtil.getExtension(file.name);
  e.target.value = null;

  return { file, extension };
}

function handleResizeFile(file, fileExtension, exif, uploadMethod) {
  return new Promise((resolve) => {
    ImageTools.resize(file, dataModel.mobileBridge.isMobile ? null : { height: MiscConstants.MAX_PHOTO_WIDTH_OR_HEIGHT, width: MiscConstants.MAX_PHOTO_WIDTH_OR_HEIGHT }, exif, (resizedImage, result, exifData) => {
      resolve(parseFile(file, fileExtension, resizedImage, exifData, uploadMethod));
    });
  });
}

function parseFile(file, fileExtension, resizedImage, exifData, uploadMethod) {
  let reader = new FileReader();
  if (resizedImage.size > 26214400) {
    dataModel.displayWarnErrorMessage(
        'File upload size limit is 25 MB. Please upload a smaller file',
        4000,
        2
      );
  } else {
    if (file) {
      reader.onloadend = () => {
        const fileInfo = {
          file: file,
          imagePreviewUrl: reader.result,
          fileExtension: fileExtension,
          exifData: exifData,
        };
        return fileInfo;
      };
      if (uploadMethod == 'library' || uploadMethod == 'web') {
        const previewFile = {
          file: file,
          fileExtension: fileExtension,
          resizedImage: resizedImage,
          type: 'image',
          exifData: exifData,
        };
        return previewFile;
      } 

      else if (uploadMethod == 'camera') {
        const previewFile = {
          file: file,
          fileExtension: fileExtension,
          resizedImage: resizedImage,
          exifData: exifData,
        };

        return previewFile;
      }
    }
  }
}

async function handleSaveFile(file, fileExtension, resizedImage, exifData, completionStep, task) {

  const partialAttachmentName = FileUtil.replaceSpecialCharacters(`${task.name}_${completionStep.name}_`);
  const attachmentName =  partialAttachmentName + determineSaveNumber(partialAttachmentName, '.' + fileExtension, completionStep) + '.' + fileExtension;
  const eventMetadatum = Metadata.generateMetadatum({ exifData: exifData });
  
  const clonedStep = _.cloneDeep(completionStep);
  let newAttachment = {};
  newAttachment.id = dataModel.attachmentsDAO.creationIndex;
  newAttachment.attachable_type = 'CompletionCriterion';
  newAttachment.attachable_id = completionStep.id;
  newAttachment.upload = resizedImage;
  newAttachment.name = attachmentName;
  clonedStep.attachments.push(newAttachment);
  return dataModel.attachmentsDAO.createAttachment(newAttachment, eventMetadatum, { recordDescriptor: { attachment: { user: { } } } }).then(attachment => {
    return clonedStep;
  });
}

function determineSaveNumber(currentStepName, fileExtension, completionStep) {
  let attachmentNames = completionStep.attachments.map(attachment => attachment.name);

  for (let i = 1; i <= MiscConstants.MAX_NUMBER_OF_PHOTOS; i++) {
    let completeStepName = currentStepName + i + fileExtension;
    if (!attachmentNames.includes(completeStepName)) {
      return i;
    }
  }
}

function captureMobileImage(uploadMethod, countdown, callback) {
  const options = setUploadOptions(uploadMethod, countdown);

  return new Promise((resolve, reject) => {
    
    // NOTE: the takingPicture nonsense below is to help identify when we
    // are resuming the app from the camera plugin (android particularly
    // likes to pause/resume the webview when the plugin UI is displayed
    // which if left to go through the normal onResume behavior can cause
    // issues such as components getting re-rendered, etc. So we set this
    // flag in local storage when entering the plugin and ensure it is removed
    // upon plugin exit

    const onSuccess = async (results) => {
      delete localStorage.takingPicture;
      if (callback) {
        callback();
      }
      try {
        results = await processMobileImageResults(results);
      } catch(e) {
        results = { filename: results };
      }
      getFileObject(results.filename, (imageObject, name) => {
        imageObject.name = fixExtension(imageObject.name);
        imageObject.filename = results.filename;
        imageObject.path = results.path;
        const fileExtension = FileUtil.getExtension(imageObject.name);
        resolve(handleResizeFile(imageObject, fileExtension, results.json_metadata ? JSON.parse(results.json_metadata) : null, uploadMethod));
      });
    };

    const onError = (err) => {
      delete localStorage.takingPicture;
      reject(new Error({ message: `There was a problem capturing an image from the mobile environment: ${err}` }));
    };

    localStorage.takingPicture = true;
    navigator.camera.getPicture(onSuccess, onError, options);
  });
}

function fixExtension(extension) {
  if(extension) {
    if (extension.match(/^.+\.[a-zA-Z]+\?[a-zA-Z0-9]+$/)) {
      let markStart = extension.lastIndexOf('?');
      if (markStart >= 0) {
        extension = extension.substring(0, markStart);
      }
    }
  }
  return extension;
}

function setUploadOptions(uploadMethod, countdown) {
  let cameraOptions;
  if (uploadMethod == 'library') {
    cameraOptions = { sourceType: 0, destinationType: 1, encodingType: 0, correctOrientation: true, targetWidth: MiscConstants.MAX_PHOTO_WIDTH_OR_HEIGHT, targetHeight: MiscConstants.MAX_PHOTO_WIDTH_OR_HEIGHT  };
  }
  if (uploadMethod == 'camera') {
    cameraOptions = {
      correctOrientation: true,
      destinationType: 1,
      encodingType: 0,
      quality: 60,
      mediaType: 0,
      saveToPhotoAlbum: true,
      sourceType: 1,
      targetWidth: MiscConstants.MAX_PHOTO_WIDTH_OR_HEIGHT,
      targetHeight: MiscConstants.MAX_PHOTO_WIDTH_OR_HEIGHT,
      toggleLoading: false,
    };
    if (countdown) {
      cameraOptions.countdown = (typeof countdown == 'boolean') ? 9 : countdown;
    }
  }
  return cameraOptions;
}

async function processMobileImageResults(results) {
  try {
    results = JSON.parse(results);
    if (MobileFiles.available() && results.filename) {
      // copy file from temporary storage to a persistent area (in case
      // offline and a rehydrate from redux occurs before the upload can
      // take place... this will prevent upload from being removed by the
      // mobile os)
      try {
        results.filename = await MobileFiles.persistFile(results.filename);
        results.path = MobileFiles.getPathWithoutAppSpecificPart(results.filename);
      } catch (error) {
        // for now leave filename as it is
      }
    }
  } catch(e) {
    results = { filename: results };
  }
  return results;
}

export default {
  handleFileSelect,
  handleResizeFile,
  handleSaveFile,
  captureMobileImage,
  parseFile,
  fixExtension,
  processMobileImageResults,
};



// WEBPACK FOOTER //
// ./src/helpers/PhotoUtilPlugin/PhotoUtilPlugin.js